import React, { useState, useRef, useEffect } from 'react';
import { Check, ChevronDown, X } from 'react-feather';
import { RequestStatus } from '@/graphql/__generated__/van-credits';
import { useTranslations } from 'use-intl';
import Button from '@/UIlibrary/Button/Button';
import { RequestStatusBadge } from './RequestStatusBadge';

interface StatusOption {
	value: RequestStatus;
	label: string;
}

interface MultiSelectVanCreditStatusProps {
	selectedStatuses: RequestStatus[];
	onChange: (statuses: RequestStatus[]) => void;
	statusOptions: StatusOption[];
	className?: string;
	placeholder?: string;
	label?: string;
	showAllSelected?: boolean;
}

const MultiSelectVanCreditStatus: React.FC<MultiSelectVanCreditStatusProps> = ({
	selectedStatuses,
	onChange,
	statusOptions,
	className = '',
	placeholder = 'Select statuses',
	label = '',
	showAllSelected = false
}) => {
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [tempSelectedStatuses, setTempSelectedStatuses] =
		useState<RequestStatus[]>(selectedStatuses);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const t = useTranslations('VanCreditRequests');

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setIsOpen(false);
				setTempSelectedStatuses(selectedStatuses);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [selectedStatuses]);

	useEffect(() => {
		setTempSelectedStatuses(selectedStatuses);
	}, [selectedStatuses]);

	const toggleStatus = (status: RequestStatus) => {
		const newSelection = tempSelectedStatuses.includes(status)
			? tempSelectedStatuses.filter((s) => s !== status)
			: [...tempSelectedStatuses, status];
		setTempSelectedStatuses(newSelection);
	};

	const clearSelection = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		setTempSelectedStatuses([]);
		onChange([]); // Immediately trigger the onChange with empty array
	};

	const handleConfirm = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		onChange(tempSelectedStatuses);
		setIsOpen(false);
	};

	const handleCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		setTempSelectedStatuses(selectedStatuses);
		setIsOpen(false);
	};

	const getAllSelectedLabels = () => {
		const selectedOptions = statusOptions.filter((option) =>
			selectedStatuses.includes(option.value)
		);

		const selectedLabels = [];
		for (let i = 0; i < selectedOptions.length; i++) {
			if (selectedOptions[i].label.length < 10) {
				selectedLabels.push(selectedOptions[i].label);
			} else {
				selectedLabels.push(`${selectedOptions[i].label.slice(0, 9)}...`);
			}
		}

		return selectedLabels ? selectedLabels.join(', ') : placeholder;
	};

	const getSelectedLabel = () => {
		if (selectedStatuses.length === 0) {
			return placeholder;
		}
		if (showAllSelected) {
			return getAllSelectedLabels();
		}
		if (selectedStatuses.length > 1) {
			return `${selectedStatuses.length} ${t('Selected')}`;
		}
		const selectedOptions = statusOptions.filter((option) =>
			selectedStatuses.includes(option.value)
		);
		return selectedOptions.length > 0 ? selectedOptions[0].label : placeholder;
	};

	return (
		<div>
			<label className="text-sm mb-2">{label}</label>
			<div className={`relative w-72 ${className}`} ref={dropdownRef}>
				<button
					type="button"
					onClick={() => setIsOpen(!isOpen)}
					className="w-full px-4 py-2 text-left bg-white border rounded-lg shadow-sm flex items-center justify-between hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
					aria-haspopup="listbox"
					aria-expanded={isOpen}
					data-testid="status-multiselect"
				>
					<span className={`truncate ${showAllSelected ? 'text-xs' : ''}`}>
						{getSelectedLabel()}
					</span>
					<div className="flex items-center">
						{selectedStatuses.length > 0 && (
							<button
								type="button"
								onClick={clearSelection}
								className="p-1 hover:bg-gray-100 rounded-full mr-1"
								aria-label="Clear selection"
							>
								<X size={14} />
							</button>
						)}
						<ChevronDown size={16} />
					</div>
				</button>

				{isOpen && (
					<div
						className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg"
						role="listbox"
						aria-multiselectable="true"
					>
						<div className="max-h-60 overflow-auto">
							{statusOptions.map((option) => (
								<div
									key={option.value}
									onClick={() => toggleStatus(option.value)}
									className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer"
									role="option"
									aria-selected={tempSelectedStatuses.includes(option.value)}
								>
									<div className="flex items-center justify-center w-5 h-5 border rounded mr-2">
										{tempSelectedStatuses.includes(option.value) && (
											<Check size={14} className="text-blue-600" />
										)}
									</div>
									<RequestStatusBadge status={option.value} size="sm" />
								</div>
							))}
						</div>
						<div className="border-t p-2 flex justify-end gap-2 bg-gray-50">
							<Button label={t('Cancel')} onClick={handleCancel} color="gray" />
							<Button label={t('Apply')} onClick={handleConfirm} />
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default MultiSelectVanCreditStatus;
